import React, { useMemo } from 'react'
import { PiCirclesThree, PiHouseLine, PiLinkSimpleLight, PiPathFill, PiQrCodeFill } from 'react-icons/pi'
import { Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import SpotIcon from '../components/SpotIcon'
import { useFeatureAuth, useFeatureFlag } from '../hooks/authorization'
import { useScreenBreakpoint } from '../hooks/toolkit'
import { FEATURES, FEATURE_FLAGS } from '../utils/constants/features.constants'
import HeaderSpace from './HeaderSpace'
import Sidebar from './Sidebar'
import Tabs from './Tabs'

const DashboardLayout: React.FC = () => {
  const { t } = useTranslation()
  const experienceEditorAuth = useFeatureAuth(FEATURES.EXP_EDITOR_UI)
  const accessCodeAuth = useFeatureAuth(FEATURES.EXP_ACCESS_CODES)
  const patternsCreateAuth = useFeatureAuth(FEATURES.CREATE_PATTERNS)
  const agamiIndoor = useFeatureFlag(FEATURE_FLAGS.AGAMI_INDOOR)
  const landingPages = useFeatureFlag(FEATURE_FLAGS.LANDING_PAGES)
  const { pathname } = useLocation()
  const screenBreakpoint = useScreenBreakpoint()
  const links = useMemo(
    () => [
      {
        label: 'Agami',
        to: '/',
        Icon: SpotIcon,
        activeLinkCondition: pathname.includes('/spot/'),
        testId: 'agami-library-dashboard-link',
      },

      ...(experienceEditorAuth
        ? [
            {
              label: t('navigation.experiences'),
              to: '/experiences',
              Icon: PiPathFill,
              activeLinkCondition: pathname.includes('/experiences/'),
              testId: 'exp-library-dashboard-link',
            },
          ]
        : []),

      ...(patternsCreateAuth
        ? [
            {
              label: t('navigation.patterns'),
              to: '/patterns',
              Icon: PiCirclesThree,
              activeLinkCondition: pathname.includes('/patterns/'),
              testId: 'patterns-dashboard-link',
            },
          ]
        : []),

      ...(accessCodeAuth
        ? [
            {
              label: t('navigation.accessCodes'),
              to: '/access-codes',
              Icon: PiQrCodeFill,
              activeLinkCondition: pathname.includes('/access-codes/'),
              testId: 'access-codes-dashboard-link',
            },
          ]
        : []),

      ...(agamiIndoor
        ? [
            {
              label: t('navigation.agamiIndoor'),
              to: '/agami-indoor',
              Icon: PiHouseLine,
              activeLinkCondition: pathname.includes('/agami-indoor/'),
              testId: 'agami-indoor-dashboard-link',
            },
          ]
        : []),

      ...(landingPages
        ? [
            {
              label: t('navigation.landingPages'),
              to: '/landing-pages',
              Icon: PiLinkSimpleLight,
              activeLinkCondition: pathname.includes('/landing-pages'),
              testId: 'landing-pages-dashboard-link',
            },
          ]
        : []),
    ],
    [experienceEditorAuth, accessCodeAuth, patternsCreateAuth, agamiIndoor, landingPages, pathname, t]
  )

  return (
    <>
      <HeaderSpace />
      <div className="relative w-full min-h-screen flex flex-col md:flex-row items-stretch z-10 dashboardlayout dark:bg-primary">
        {screenBreakpoint === 'sm' ? <Tabs links={links} /> : <Sidebar links={links} />}
        <div className="w-full min-w-0 bg-base-gray-400 min-h-screen dark:bg-primary-400">
          <div className="w-full min-w-0 mx-auto mt-2 md:mt-6 text-primary px-3 3xl:px-6 dark:text-white">
            <Outlet />
          </div>
        </div>
      </div>
    </>
  )
}

export default DashboardLayout
